import { QRCodeSVG } from 'qrcode.react';
import { FileText, Globe, MapPin, Phone } from 'lucide-react';
import { formatCalendarDate, formatDate } from '../../dateFormat';
import { FitLogo } from './FitLogo';
import { useGeoPicker } from '../../geo-picker/picker';
import {
  TranscriptProgramEnrollment,
  TranscriptUser,
} from './TranscriptView';

interface Props {
  user: TranscriptUser;
  programEnrollments: TranscriptProgramEnrollment[];
  headingText: string;
  /** When set, renders a verification QR code (official transcript only). */
  verifyUrl?: string;
  /** When the QR's secure link expires; shown as its caption when known. */
  expiresAt?: Date;
  /**
   * The date this document reflects: the issue date for frozen (issued)
   * transcripts, the viewing date otherwise.
   */
  documentDate?: Date;
  /**
   * Pre-resolved address label. Server-side PDF rendering passes this because
   * the geo picker resolves country codes in a browser-only effect; when
   * provided it is used verbatim instead of the client-side lookup.
   */
  resolvedAddress?: string | null;
}

// A birth date is a calendar day, so it must not be routed through
// `formatDate`, which resolves an instant in whatever timezone it runs in.
// This component renders in the learner's browser *and* server-side for the
// issued PDF; the two would otherwise print different days for the same person.
const formatShortDate = (value: unknown): string | null =>
  formatCalendarDate(value as string) || null;

const TranscriptHeader = ({
  user,
  programEnrollments,
  headingText,
  verifyUrl,
  expiresAt,
  documentDate = new Date(),
  resolvedAddress,
}: Props) => {
  const dob = formatShortDate(user.birthDate);
  const { getCountryByCode } = useGeoPicker();
  const address =
    resolvedAddress !== undefined
      ? resolvedAddress
      : user.countryOfOrigin
      ? getCountryByCode(user.countryOfOrigin)?.label ?? user.countryOfOrigin
      : null;

  return (
    <div className="relative w-full flex flex-col items-center gap-4">
      {/* Institution masthead; the QR sits in the top-right corner (per
          design) on wide/print layouts and drops into the flow on mobile. */}
      <FitLogo className="h-20 md:h-24 print:h-24" />
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="hidden sm:block print:block w-11 border-t border-foreground/70"
        />
        <h2 className="text-lg md:text-xl font-medium uppercase tracking-wide text-center text-foreground">
          {headingText}
        </h2>
        <span
          aria-hidden
          className="hidden sm:block print:block w-11 border-t border-foreground/70"
        />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs md:text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-4 w-4 shrink-0" />
          140 E Broadway Ave #25 Jackson, WY 83001
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Globe className="h-4 w-4 shrink-0" />
          frontiertech.edu
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Phone className="h-4 w-4 shrink-0" />
          +1 (307) 264-2843
        </span>
      </div>
      {verifyUrl && (
        <div className="flex flex-col items-center gap-1 lg:absolute lg:right-0 lg:top-0 print:absolute print:right-0 print:top-0">
          <QRCodeSVG value={verifyUrl} size={88} />
          <span className="text-xs text-foreground">
            {expiresAt ? `Expires ${formatDate(expiresAt)}` : 'Scan to verify'}
          </span>
        </div>
      )}

      {/* Student card: degree/status on the left, identity on the right. */}
      <div className="w-full border border-border rounded-[10px] px-6 py-5 mt-2 flex flex-col md:flex-row print:flex-row items-center justify-center gap-5 md:gap-14 print:gap-14">
        <div className="flex flex-col gap-4 md:w-2/5 print:w-2/5">
          {programEnrollments.map((pe, i) => (
            <div key={i} className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
                <FileText className="h-5 w-5" />
              </span>
              <div className="text-sm flex flex-col gap-1 min-w-0">
                <p className="text-foreground">
                  <span className="font-semibold">Degree: </span>
                  {pe.program.name}
                </p>
                {pe.status === 'GRADUATED' ? (
                  <p className="text-success">
                    <span className="font-semibold">
                      {pe.graduatedAt ? 'Granted as of: ' : 'Granted'}
                    </span>
                    {pe.graduatedAt ? (
                      <span className="font-medium">
                        {formatDate(pe.graduatedAt as string)}
                      </span>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    <span className="font-semibold">Ongoing as of: </span>
                    {formatDate(documentDate)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div
          aria-hidden
          className="hidden md:block print:block w-px self-stretch bg-border"
        />
        <div className="flex flex-col items-center md:items-end print:items-end gap-1.5 md:w-2/5 print:w-2/5 min-w-0">
          <p className="text-xl md:text-2xl print:text-xl font-semibold text-foreground break-words text-center md:text-right print:text-right">
            {user.firstName} {user.lastName}
          </p>
          <div className="text-xs md:text-sm leading-relaxed text-center md:text-right print:text-right w-full">
            <p className="break-words">
              <span className="text-muted-foreground">Email: </span>
              <span className="text-foreground">{user.primaryEmail}</span>
            </p>
            {user.sandId && (
              <p className="break-all">
                <span className="text-muted-foreground">Student ID: </span>
                <span className="text-foreground">{user.sandId}</span>
              </p>
            )}
            {dob && (
              <p>
                <span className="text-muted-foreground">D.O.B.: </span>
                <span className="text-foreground">{dob}</span>
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Address: </span>
              <span className="text-foreground">{address ?? '—'}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptHeader;
